import React, { ReactElement, RefObject, useEffect, useMemo, useReducer, useRef } from 'react';

import { useApplicationBuildContext } from '@context/ApplicationBuildContext';
import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { EScriptStatus, useScript } from '@hooks';

import { useCartPageContext } from '../../../context';
import { EOrderStatus } from '../../../model';
import { PayPalExpressForm } from '../components';
import { defaultState, reducer, useActions } from '../reducer';
import { getPayPalConnectionString, initializeButtons } from '../utils';

interface IUseInitializePaypalButton {
  isPayPalReady: boolean;
  paypalForm: ReactElement | null;
}

export const useInitializePaypalButton = (containerRef: RefObject<HTMLDivElement>): IUseInitializePaypalButton => {
  const submitFormRef = useRef<HTMLFormElement>(null);
  const [orderState, dispatch] = useReducer(reducer, defaultState);
  const { handleOrderCreate, handlePayPalError, handleOrderApprove } = useActions(dispatch);

  const {
    settings: { websiteHostAddress },
  } = useApplicationBuildContext();

  const {
    applicationSettings: {
      primaryCurrency: { currency },
    },
  } = useApplicationRuntimeContext();

  const {
    settings: {
      paypal: { expressCheckout, clientId },
    },
  } = useCartPageContext();

  const { status, load } = useScript(getPayPalConnectionString(clientId, currency));

  useEffect(() => {
    if (expressCheckout && clientId) {
      load();
    }
  }, [load, expressCheckout, clientId]);

  useEffect(() => {
    if (status === EScriptStatus.READY && containerRef.current) {
      initializeButtons({
        onApprove: handleOrderApprove,
        createOrder: handleOrderCreate,
        onError: handlePayPalError,
        containerRef: containerRef,
      });
    }
  }, [status, containerRef, handleOrderCreate, handlePayPalError, handleOrderApprove]);

  useEffect(() => {
    if (submitFormRef.current && orderState.status === EOrderStatus.APPROVED) {
      submitFormRef.current.submit();
    }
  }, [orderState]);

  return useMemo(
    () => ({
      isPayPalReady: status === EScriptStatus.READY && expressCheckout,
      paypalForm: orderState && (
        <PayPalExpressForm baseUrl={websiteHostAddress} formRef={submitFormRef} {...orderState} />
      ),
    }),
    [orderState, submitFormRef, expressCheckout, status, websiteHostAddress],
  );
};
